import { useEffect, useState } from 'react';
import { useAuthStore } from './stores/authStore';
import { initializeOfflineDB } from './lib/db';
import LoginPage from './pages/LoginPage';
import TillPage from './pages/TillPage';
import ManagerDashboard from './pages/ManagerDashboard';
import { AdminPortal } from './pages/AdminPortal';
import { PortalNavigation } from './components/PortalNavigation';
import MobileStaffApp from './components/MobileStaffApp';
import CounterPage from './pages/CounterPage';
import './index.css';

function App() {
  const { isAuthenticated, staff } = useAuthStore();
  const [currentPage, setCurrentPage] = useState<string>('home');

  useEffect(() => {
    initializeOfflineDB();
    // Check URL for specific pages
    const path = window.location.pathname;
    if (path.includes('admin')) setCurrentPage('admin');
    else if (path.includes('staff')) setCurrentPage('staff');
    else if (path.includes('counter')) setCurrentPage('counter');
  }, []);

  // Keep the address bar in step so a portal can be linked to or reloaded
  const navigate = (portal: string) => {
    setCurrentPage(portal);
    const path = portal === 'home' || portal === 'till' ? '/' : `/${portal}`;
    window.history.pushState({}, '', path);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoginPage />
      </div>
    );
  }

  // The till is full-screen by design (it is a working surface, not a page),
  // so it gets no portal bar — staff switch away from it via the Staff App.
  const OVERSIGHT_ROLES = ['MANAGER', 'VIEWER'];
  const isTill =
    currentPage === 'till' ||
    (currentPage === 'home' && !OVERSIGHT_ROLES.includes(staff?.role || ''));
  // Crates and prepayments are counter jobs, so they hang off the till —
  // cashiers never reach the manager dashboard.
  if (currentPage === 'counter') {
    return <CounterPage onBack={() => navigate('till')} />;
  }

  if (isTill) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TillPage
          onOpenStaffApp={() => navigate('staff')}
          onOpenCounter={() => navigate('counter')}
        />
      </div>
    );
  }

  const screen =
    currentPage === 'admin' && staff?.role === 'MANAGER' ? <AdminPortal /> :
    currentPage === 'staff' ? <MobileStaffApp /> :
    <ManagerDashboard />;

  return (
    <div className="min-h-screen bg-gray-50">
      <PortalNavigation
        userRole={staff?.role || 'CASHIER'}
        currentPortal={currentPage === 'home' ? 'home' : currentPage}
        onNavigate={navigate}
      />
      {screen}
    </div>
  );
}

export default App;
